import {
  err,
  normalizeContentRegion,
  ok,
  parsePopularFeed,
  parseTrendingFeed,
  resolveInstanceUrl,
  withQuery,
  type AbortLike,
  type ParsedList,
  type Result,
  type VideoSummary,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { fetchJson } from './json';
import type { ApiProblem } from './videos';

/**
 * §8's Home/Popular row. Two endpoints, one shape: a bare JSON array of video records.
 *
 * R4's drop count comes back with the items rather than being thrown away — "this feed is empty" and
 * "we discarded every row" are different states, and the page says which.
 */
export type FeedResult = Result<ParsedList<VideoSummary>, ApiProblem>;

export async function fetchPopular(platform: Platform, signal?: AbortLike): Promise<FeedResult> {
  const path = '/api/v1/popular';
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });
  return await fetchVideoFeed(platform, url, parsePopularFeed, signal);
}

/**
 * `region` is validated in core before it can reach the wire: an unsupported code answers **500**
 * with a leaked Crystal `KeyError`, and so does an empty `region=` (`feed/region.ts`). An
 * unrecognised setting therefore *omits* the parameter, which is what the instance's own default
 * already means — measured: `?region=US` and a bare call return byte-identical lists.
 */
export async function fetchTrending(
  platform: Platform,
  region: string | null,
  signal?: AbortLike,
): Promise<FeedResult> {
  const path = '/api/v1/trending';
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, { region: normalizeContentRegion(region) });
  if (url === null) return err({ kind: 'bad-url', path });

  return await fetchVideoFeed(platform, url, parseTrendingFeed, signal);
}

async function fetchVideoFeed(
  platform: Platform,
  url: string,
  parse: (raw: unknown, ctx: Platform['parseContext']) => ParsedList<VideoSummary>,
  signal?: AbortLike,
): Promise<FeedResult> {
  // Through `api/json.ts` since 4a, which is where the zero-byte `200` is told apart from a body that
  // is not JSON — two failures this module used to report with one sentence about a proxy.
  const raw = await fetchJson(platform, url, signal);
  if (!raw.ok) return err(raw.error);

  const parsed = parse(raw.value, platform.parseContext);
  // A list whose *envelope* was wrong — an object where an array belongs — drops one error and no
  // items, and that is a parse failure rather than an empty feed. A list that dropped some of its
  // rows is not: those are counted and shown (R4).
  if (parsed.items.length === 0 && parsed.dropped.length === 1) {
    const error = parsed.dropped[0];
    if (error !== undefined && error.reason === 'wrong-type' && error.field === null) {
      return err({ kind: 'parse', error });
    }
  }

  return ok(parsed);
}
